import { Add as AddIcon, Delete as DeleteIcon } from "@mui/icons-material";
import {
  Autocomplete,
  Divider,
  Grid,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { makeNewId } from "common/ids";
import Component from "components/Component";
import Button from "components/common/Button";
import Disabled from "components/common/Disabled";
import FormattedDatePicker from "components/common/FormattedDatePicker";
import PrettyNumberTextField, {
  getStringValue,
} from "components/common/PrettyNumberTextField";
import { usePricing } from "components/customPricing/dnfModel1/pricingHooks";
import LayerNavigator from "components/pricing/modelPricing/LayerNavigator";
import ModelPricingResults from "components/pricing/modelPricing/ModelPricingResults";
import { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as analyticsService from "services/analyticsService";
import * as programActions from "store/actions/input/program/programActions";
import * as programPricingSlice from "store/programPricing";
import {
  selectActiveLayerIndex,
  selectResolvedLayers,
} from "store/selectors/pricing/pricingSelectors";
import * as submissionSelector from "store/selectors/submission/submissionSelector";
import * as userSelectors from "store/selectors/user/userSelectors";

// A `percent` field is stored as the decimal the workbook wants and shown as the percentage a
// user thinks in. Rounded because 0.1 * 100 is 10.000000000000002 in floating point.
const toPercent = (value) =>
  value == null ? null : Math.round(value * 1e8) / 1e6;
const fromPercent = (value) =>
  value == null ? null : Math.round(value * 1e6) / 1e8;

// value validation from the field schema (Excel data-validation constraints)
const fieldError = (field, value) => {
  const empty = value == null || value === "";
  if (field.required && empty) return "Required";
  if (empty) return null;
  if (field.type === "enum") {
    const options = field.options ?? [];
    return options.length && !options.includes(value)
      ? "Not a valid option"
      : null;
  }
  if (field.type === "number" || field.type === "percent") {
    const n = Number(value);
    if (Number.isNaN(n)) return "Must be a number";
    if (field.integer && !Number.isInteger(n)) return "Must be a whole number";
    if (field.exclusiveMin != null && n <= field.exclusiveMin)
      return `Must be > ${field.exclusiveMin}`;
    if (field.exclusiveMax != null && n >= field.exclusiveMax)
      return `Must be < ${field.exclusiveMax}`;
    if (field.min != null && n < field.min) return `Must be ≥ ${field.min}`;
    if (field.max != null && n > field.max) return `Must be ≤ ${field.max}`;
  }
  return null;
};

// Rows need an identity that survives a delete: keyed by index, MUI reuses the input that held
// the removed row's text for whatever row slides up into its place. Inert for the backend — a
// mapping only reads the columns it declares, so this key is never written to a cell.
const ROW_ID = "__rowId";

const gridFieldError = (field, rows) =>
  (rows ?? []).some((row) =>
    (field.columns ?? []).some(
      (column) => fieldError(column, row?.[column.key]) != null
    )
  );

// read/write a `$.program.*` bind path so a field stays in sync with the program tabs
const bindParts = (path) => path.replace(/^\$\.?/, "").split(".");
const readPath = (root, path) =>
  bindParts(path).reduce(
    (obj, key) => (obj == null ? undefined : obj[key]),
    root
  );

const TF = {
  variant: "filled",
  size: "small",
  fullWidth: true,
  InputLabelProps: { shrink: true },
};

const CELL_PROPS = {
  hiddenLabel: true,
  size: "small",
  variant: "filled",
  fullWidth: true,
};

const HEADER_SX = {
  border: "none",
  color: "var(--mui-brand-gray-muted)",
  padding: "6px",
  fontWeight: 500,
};

const BODY_SX = {
  border: "none",
  padding: "6px",
};

const SectionTitle = ({ children }) => (
  <Typography
    variant={"overline"}
    color={"text.secondary"}
    sx={{ letterSpacing: 1, lineHeight: 1, fontSize: "0.7rem" }}
  >
    {children}
  </Typography>
);

const ScalarField = ({ field, value, onChange, disabled }) => {
  const error = fieldError(field, value);
  const helperText = error ?? field.help;

  if (field.type === "enum") {
    const options = field.options ?? [];
    return (
      <Autocomplete
        options={options}
        getOptionLabel={(option) => String(option)}
        value={options.includes(value) ? value : null}
        disabled={disabled}
        onChange={(_, option) => onChange(option ?? null)}
        renderInput={(params) => (
          <TextField
            {...params}
            {...TF}
            label={field.label}
            error={error != null}
            helperText={helperText}
          />
        )}
        autoHighlight
      />
    );
  }
  if (field.type === "number") {
    return (
      <PrettyNumberTextField
        {...TF}
        label={field.label}
        error={error != null}
        helperText={helperText}
        value={value}
        // seeds the editable string so an invalid stored value stays visible
        stringValue={getStringValue(value)}
        disabled={disabled}
        onChangeNumber={onChange}
      />
    );
  }
  if (field.type === "percent") {
    return (
      <PrettyNumberTextField
        {...TF}
        label={field.label}
        error={error != null}
        helperText={helperText}
        suffix={"%"}
        value={toPercent(value)}
        stringValue={getStringValue(toPercent(value))}
        disabled={disabled}
        onChangeNumber={(n) => onChange(fromPercent(n))}
      />
    );
  }
  if (field.type === "date") {
    return (
      <FormattedDatePicker
        label={field.label}
        value={value}
        disabled={disabled}
        onChangeDate={onChange}
        helperText={helperText}
        slotProps={{ textField: { fullWidth: true, error: error != null } }}
      />
    );
  }
  // Only "string" falls through to free text. An unrecognised type means the model declares
  // something this build does not render (config ahead of the deploy, or a typo); showing an
  // editable text box would capture a value the mapping cannot use.
  if (field.type != null && field.type !== "string") {
    return (
      <TextField
        {...TF}
        label={field.label}
        value={""}
        disabled
        helperText={`Unsupported field type "${field.type}"`}
      />
    );
  }
  return (
    <TextField
      {...TF}
      label={field.label}
      error={error != null}
      helperText={helperText}
      value={value ?? ""}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value === "" ? null : e.target.value)}
    />
  );
};

const GridCell = ({ column, value, onChange, disabled }) => {
  const error = fieldError(column, value);

  if (column.type === "enum") {
    const options = column.options ?? [];
    return (
      <Autocomplete
        size={"small"}
        fullWidth
        options={options}
        getOptionLabel={(option) => String(option)}
        value={options.includes(value) ? value : null}
        disabled={disabled}
        onChange={(_, option) => onChange(option ?? null)}
        renderInput={(params) => (
          <TextField
            {...params}
            hiddenLabel
            variant={"filled"}
            error={error != null}
            inputProps={{ ...params.inputProps, "aria-label": column.label }}
          />
        )}
        autoHighlight
      />
    );
  }
  if (column.type === "number") {
    return (
      <PrettyNumberTextField
        {...CELL_PROPS}
        inputProps={{ "aria-label": column.label }}
        error={error != null}
        title={error ?? undefined}
        value={value}
        stringValue={getStringValue(value)}
        disabled={disabled}
        onChangeNumber={onChange}
      />
    );
  }
  if (column.type === "percent") {
    return (
      <PrettyNumberTextField
        {...CELL_PROPS}
        inputProps={{ "aria-label": column.label }}
        error={error != null}
        title={error ?? undefined}
        suffix={"%"}
        value={toPercent(value)}
        stringValue={getStringValue(toPercent(value))}
        disabled={disabled}
        onChangeNumber={(n) => onChange(fromPercent(n))}
      />
    );
  }
  if (column.type === "date") {
    return (
      <FormattedDatePicker
        compact
        value={value}
        disabled={disabled}
        onChangeDate={onChange}
        slotProps={{ textField: { hiddenLabel: true } }}
      />
    );
  }
  return (
    <TextField
      {...CELL_PROPS}
      inputProps={{ "aria-label": column.label }}
      value={value ?? ""}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value === "" ? null : e.target.value)}
    />
  );
};

/** The layer a model prices against. Every layer is sent to the workbook; this picks which one
 *  the tab is looking at, using the shared active layer so it stays in step with the other
 *  pricing tabs. Layers are added, removed and edited on the Layer Pricing tab. */
const LayerField = ({ field }) => (
  <Stack spacing={1}>
    <SectionTitle>{field.label ?? "Layer"}</SectionTitle>
    {field.help && (
      <Typography variant={"caption"} color={"text.secondary"}>
        {field.help}
      </Typography>
    )}
    <LayerNavigator />
  </Stack>
);

const GridFieldTable = ({ field, rows, onChange, disabled }) => {
  const columns = field.columns ?? [];
  const atCapacity = field.rows != null && rows.length >= field.rows;

  const updateRow = (index, patch) =>
    onChange(rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));

  const removeRow = (index) => onChange(rows.filter((_, i) => i !== index));

  const addRow = () =>
    onChange([
      ...rows,
      {
        [ROW_ID]: makeNewId(),
        ...Object.fromEntries(columns.map((column) => [column.key, null])),
      },
    ]);

  return (
    <Stack spacing={1}>
      <SectionTitle>{field.label}</SectionTitle>
      {field.help && (
        <Typography variant={"caption"} color={"text.secondary"}>
          {field.help}
        </Typography>
      )}
      <TableContainer>
        <Table size={"small"} aria-label={field.label}>
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell key={column.key} sx={{ ...HEADER_SX, minWidth: 90 }}>
                  {column.label}
                </TableCell>
              ))}
              <TableCell sx={{ ...HEADER_SX, width: 40 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length + 1} sx={BODY_SX}>
                  <Typography variant={"body2"} color={"text.secondary"}>
                    {"No rows yet. Add a row to start."}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row, index) => (
                <TableRow key={row?.[ROW_ID] ?? index}>
                  {columns.map((column) => (
                    <TableCell key={column.key} sx={BODY_SX}>
                      <GridCell
                        column={column}
                        value={row?.[column.key] ?? null}
                        disabled={disabled}
                        onChange={(value) =>
                          updateRow(index, { [column.key]: value })
                        }
                      />
                    </TableCell>
                  ))}
                  <TableCell sx={BODY_SX}>
                    <IconButton
                      size={"small"}
                      aria-label={"Delete row"}
                      disabled={disabled}
                      onClick={() => removeRow(index)}
                    >
                      <DeleteIcon fontSize={"small"} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Stack direction={"row"}>
        <Disabled ifReadOnly>
          <Button
            startIcon={<AddIcon />}
            onClick={addRow}
            isDisabled={atCapacity}
            tooltip={atCapacity ? `Limited to ${field.rows} rows` : null}
            tooltipRequired={atCapacity}
          >
            {"Add row"}
          </Button>
        </Disabled>
      </Stack>
    </Stack>
  );
};

const ModelPricing = ({ engine: engineProp }) => {
  const dispatch = useDispatch();
  const isReadOnly = useSelector(userSelectors.isUserReadOnly);
  const programPricing = useSelector(programPricingSlice.selectInput);

  // Pricing.js renders one tab per active registry entry and passes its engine; there is no
  // analytics-config fallback, since programPricing.engine names the built-in tabs' engine
  const engine = engineProp;

  const {
    data: engineConfig,
    isFetching: isModelFetching,
  } = analyticsService.useEngineConfigQuery({ engine }, { skip: !engine });

  const model = engineConfig?.values;
  const fields = model?.fields ?? [];
  const allInputs = programPricing?.inputs ?? {};
  // Inputs are namespaced per engine so two models with a same-named field cannot clobber each
  // other. Entries saved before the namespace existed sit at the top level; they are still read
  // as a fallback and are migrated on the next edit, so nothing already captured is lost.
  const engineInputs = (engine && allInputs[engine]) ?? {};
  const legacyInputs = allInputs;
  const inputValue = (key) => engineInputs[key] ?? legacyInputs[key];
  // a stored value whose field later changed type can be any shape; both the validation and the
  // render path must tolerate that, or `.some` throws and white-screens the tab
  const gridRows = (field) => {
    const rows = inputValue(field.key);
    return Array.isArray(rows) ? rows : [];
  };
  const resolved = useSelector(
    submissionSelector.selectResolvedSubmissionWithoutInput
  );
  const activeLayerIndex = useSelector(selectActiveLayerIndex) ?? 0;
  const layers = useSelector(selectResolvedLayers) ?? [];

  const setInput = useCallback(
    (key, value) =>
      dispatch(
        programPricingSlice.updatePathValue({
          path: engine ? ["inputs", engine, key] : ["inputs", key],
          value,
        })
      ),
    [dispatch, engine]
  );

  // The engine always prices off the pricing inputs, so that is what the tab shows and stores.
  // `bind` links a field to somewhere the submission already holds the same figure: it seeds the
  // input when nothing has been captured yet, and edits mirror back if that place exists.
  const valueFor = (field) => inputValue(field.key) ?? null;

  // Only some bind targets have a writer. Everything else seeds one way — a pricing number must
  // never invent an underwriting record (an exposure write is keyed by year and would create one).
  const canWriteBind = (field) => {
    if (!field.bind) return false;
    const parts = bindParts(field.bind);
    return parts[0] === "program" && parts.length === 2;
  };

  const changeField = (field, value) => {
    setInput(field.key, value);
    if (
      canWriteBind(field) &&
      readPath(resolved, field.bind) !== undefined // mirror it, do not create it
    ) {
      dispatch(
        programActions.updateProgram({ [bindParts(field.bind)[1]]: value })
      );
    }
  };

  // Seed bound fields once from the submission. Keyed on presence, not value, so clearing a field
  // stays cleared instead of the bound value reappearing on the next render.
  const boundFields = fields.filter((field) => field.bind);
  useEffect(() => {
    for (const field of boundFields) {
      const captured =
        Object.prototype.hasOwnProperty.call(engineInputs, field.key) ||
        Object.prototype.hasOwnProperty.call(legacyInputs, field.key);
      const bound = readPath(resolved, field.bind);
      if (!captured && bound != null) {
        setInput(field.key, bound);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [engine, boundFields.length, resolved, setInput]);

  const scalarFields = fields.filter(
    (field) => field.type !== "grid" && field.type !== "layer"
  );
  const gridFields = fields.filter((field) => field.type === "grid");
  const layerFields = fields.filter((field) => field.type === "layer");

  const sections = [];
  for (const field of scalarFields) {
    const name = field.section ?? null;
    const section = sections.find((s) => s.name === name);
    if (section) {
      section.fields.push(field);
    } else {
      sections.push({ name, fields: [field] });
    }
  }

  const hasErrors =
    scalarFields.some((field) => fieldError(field, valueFor(field)) != null) ||
    gridFields.some((field) => gridFieldError(field, gridRows(field)));

  // isolate each model's results under its own engine key so tabs don't clobber
  // each other, and never auto-price invalid inputs
  const { data: pricing, isFetching, refetch } = usePricing({
    pricingMethod: engine ?? "programPricing",
    engine,
    isEnabled: !hasErrors,
  });

  const emptyMessage = !engine
    ? "This pricing model is missing its engine name. Check the registry entry."
    : engineConfig?.failure ??
      (model == null
        ? "Unable to load the pricing model configuration."
        : fields.length === 0
        ? "The configured pricing model does not declare any input fields."
        : null);

  return (
    <Component
      title={model?.title ?? "Model Pricing"}
      loading={isModelFetching}
      isFetching={isFetching}
    >
      {emptyMessage != null ? (
        <Typography variant={"body2"} color={"text.secondary"}>
          {emptyMessage}
        </Typography>
      ) : (
        <Stack direction={"column"} gap={2} sx={{ pr: 2 }}>
          {sections.map((section) => (
            <Stack key={section.name ?? "__general"} spacing={1}>
              {section.name && <SectionTitle>{section.name}</SectionTitle>}
              <Grid container spacing={2}>
                {section.fields.map((field) => (
                  <Grid item xs={4} key={field.key}>
                    <ScalarField
                      field={field}
                      value={valueFor(field)}
                      disabled={isReadOnly}
                      onChange={(value) => changeField(field, value)}
                    />
                  </Grid>
                ))}
              </Grid>
            </Stack>
          ))}
          {layerFields.map((field) => (
            <LayerField key={field.key} field={field} />
          ))}
          {gridFields.map((field) => (
            <GridFieldTable
              key={field.key}
              field={field}
              rows={gridRows(field)}
              disabled={isReadOnly}
              onChange={(rows) => setInput(field.key, rows)}
            />
          ))}
          <Stack
            direction={"row"}
            justifyContent={"flex-end"}
            alignItems={"center"}
            width={"100%"}
            spacing={2}
          >
            <Disabled ifReadOnly>
              <Button
                onClick={refetch}
                isLoading={isFetching}
                isDisabled={hasErrors}
                tooltip={hasErrors ? "Fix the highlighted fields first" : null}
                tooltipRequired={hasErrors}
              >
                {"Calculate"}
              </Button>
            </Disabled>
          </Stack>
          <Divider />
          <ModelPricingResults
            pricing={pricing}
            resultLabels={model?.resultLabels}
            isStale={hasErrors}
            highlightRecordIndex={layerFields.length ? activeLayerIndex : null}
            // the workbook returns its full row capacity; only the layers that exist are real
            maxRecords={layerFields.length ? layers.length : null}
            // one layer at a time: show the selected layer's row, not every layer
            singleRecordIndex={layerFields.length ? activeLayerIndex : null}
          />
        </Stack>
      )}
    </Component>
  );
};

export default ModelPricing;
